import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { tocarToqueAntigo } from '../utils/somAlarme';

const PedidoAlarmeContext = createContext(null);

const INTERVALO_VERIFICACAO = 30000; // verifica pedidos pendentes a cada 30s
const INTERVALO_TOQUE       = 3500;  // repete o toque enquanto houver pendente não silenciado

export function PedidoAlarmeProvider({ children }) {
  const { usuario } = useAuth();
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/master');
  const ativo = usuario?.perfil === 'admin_empresa' && isAdminRoute;

  const [pendentes, setPendentes] = useState([]);
  const silenciadosRef = useRef(new Set());
  const toqueIntervalRef = useRef(null);

  const verificar = useCallback(async () => {
    try {
      const { data } = await api.get('/pedidos', { params: { status: 'pendente', limit: 100 } });
      const lista = data.dados || [];
      const idsAtuais = new Set(lista.map(p => p.id));
      for (const id of silenciadosRef.current) {
        if (!idsAtuais.has(id)) silenciadosRef.current.delete(id);
      }
      setPendentes(lista);
    } catch { /* falha pontual de rede não deve travar o alarme */ }
  }, []);

  useEffect(() => {
    if (!ativo) { setPendentes([]); silenciadosRef.current.clear(); return; }
    verificar();
    const id = setInterval(verificar, INTERVALO_VERIFICACAO);
    return () => clearInterval(id);
  }, [ativo, verificar]);

  const naoSilenciados = pendentes.filter(p => !silenciadosRef.current.has(p.id));
  const tocando = ativo && naoSilenciados.length > 0;

  useEffect(() => {
    if (tocando) {
      tocarToqueAntigo();
      toqueIntervalRef.current = setInterval(tocarToqueAntigo, INTERVALO_TOQUE);
    }
    return () => { if (toqueIntervalRef.current) clearInterval(toqueIntervalRef.current); };
  }, [tocando]);

  const silenciar = () => {
    pendentes.forEach(p => silenciadosRef.current.add(p.id));
    setPendentes(p => [...p]); // força recálculo de naoSilenciados/tocando
  };

  return (
    <PedidoAlarmeContext.Provider value={{
      pendentes,
      totalPendentes: pendentes.length,
      tocando,
      silenciar,
    }}>
      {children}
    </PedidoAlarmeContext.Provider>
  );
}

export const usePedidoAlarme = () => useContext(PedidoAlarmeContext);
